import { useState, useEffect } from 'react';
import { useTranslationWithSelfFallback } from '@i18n/useTranslationWithSelfFallback';
import cn from 'classnames';
import { useLocale } from '@hooks/useLocale';
import type { MenuItem, LinkColumn } from '../types';
import { MegaMenuMobileContext } from '@context/MegaMenuMobileContext';
import { RootPanel } from './RootPanel';
import { CategoryPanel } from './CategoryPanel';
import { LinksPanel } from './LinksPanel';
import styles from './Mobile.module.scss';
import { menuDataByLocale } from '../menuData';

export interface MegaMenuMobileProps {
  isOpen: boolean;
  onClose: () => void;
  top?: number;
  items?: MenuItem[];
  alternateLocaleUrl?: string;
}

export default function MegaMenuMobile({
  isOpen,
  onClose,
  top = 0,
  items,
  alternateLocaleUrl,
}: MegaMenuMobileProps) {
  const locale = useLocale();
  const t = useTranslationWithSelfFallback('common');
  const defaultMenuData =
    menuDataByLocale[locale ?? 'en'] ?? menuDataByLocale.en;
  const navItems = items ?? (defaultMenuData as MenuItem[]);

  const [depth, setDepth] = useState(0);
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null);
  const [activeColumn, setActiveColumn] = useState<LinkColumn | null>(null);

  useEffect(() => {
    document.documentElement.style.overflow = isOpen ? 'hidden' : 'auto';
    return () => {
      document.documentElement.style.overflow = 'auto';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      const timeoutId = setTimeout(() => {
        setDepth(0);
        setActiveItem(null);
        setActiveColumn(null);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [isOpen]);

  const handleItemClick = (item: MenuItem) => {
    setActiveItem(item);
    setDepth(1);
  };

  const handleColumnClick = (column: LinkColumn) => {
    setActiveColumn(column);
    setDepth(2);
  };

  const handleBack = () => {
    if (depth === 2) {
      setDepth(1);
      setActiveColumn(null);
    } else {
      setDepth(0);
      setActiveItem(null);
    }
  };

  return (
    <MegaMenuMobileContext.Provider
      value={{
        activeItem,
        activeColumn,
        handleItemClick,
        handleColumnClick,
        handleBack,
        handleClose: onClose,
      }}
    >
      <div
        className={cn(styles.Drawer, { [styles.DrawerOpen]: isOpen })}
        style={{ top }}
        aria-hidden={!isOpen}
        aria-modal={isOpen}
        aria-label={t('header.navigation.aria.label')}
        role="dialog"
      >
        <div
          className={styles.PanelTrack}
          style={{ transform: `translateX(calc(${depth} * -33.333%))` }}
        >
          <RootPanel
            items={navItems}
            locale={locale ?? 'en'}
            alternateLocaleUrl={alternateLocaleUrl}
          />
          <CategoryPanel />
          <LinksPanel />
        </div>
      </div>
    </MegaMenuMobileContext.Provider>
  );
}
